import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { NovaMark } from "@/components/landing/Logo";

const INDUSTRIES = [
  "Retail", "Services", "Agency", "Healthcare",
  "Hospitality", "Trades & Home Services", "Professional Services", "Other",
];

const TOOLS = [
  "Google Workspace", "Microsoft 365", "QuickBooks", "CRM",
  "Calendar", "Email", "Accounting", "Nothing yet — I'll use Nova's built-in tools",
];

const EASE = [0.16, 1, 0.3, 1];

export default function Onboarding() {
  const { setUser, API } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [tools, setTools] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const toggleTool = (t) => {
    setTools((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
  };

  const canContinue = step === 0 ? businessName.trim().length > 0 : step === 1 ? !!industry : true;

  const finish = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/onboarding`,
        { business_name: businessName, industry, tools },
        { withCredentials: true }
      );
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "What's your business called?",
      sub: "Nova will use this across your workspace.",
      content: (
        <input
          data-testid="onboarding-business-name-input"
          type="text"
          autoFocus
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          placeholder="e.g. Pine & Co. Studio"
          className="w-full rounded-2xl border border-slate-300 bg-white px-5 py-4 text-base text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
        />
      ),
    },
    {
      title: "Which industry are you in?",
      sub: "This helps Nova shape your workflows.",
      content: (
        <div className="flex flex-wrap gap-3">
          {INDUSTRIES.map((ind) => (
            <button
              key={ind}
              type="button"
              data-testid={`industry-chip-${ind.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setIndustry(ind)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors duration-300 ${
                industry === ind
                  ? "bg-nova-navy text-white border-nova-navy"
                  : "bg-white text-nova-slate border-slate-300 hover:border-nova-blue/40 hover:text-nova-navy"
              }`}
            >
              {ind}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: "Which tools does your business use?",
      sub: "Pick everything that applies — Nova connects to them, or replaces them.",
      content: (
        <div className="flex flex-wrap gap-3">
          {TOOLS.map((t) => {
            const active = tools.includes(t);
            return (
              <button
                key={t}
                type="button"
                data-testid={`tool-chip-${t.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}
                onClick={() => toggleTool(t)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors duration-300 ${
                  active
                    ? "bg-nova-blue text-white border-nova-blue"
                    : "bg-white text-nova-slate border-slate-300 hover:border-nova-blue/40 hover:text-nova-navy"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-nova-mist via-white to-white flex flex-col" data-testid="onboarding-page">
      <div className="absolute inset-0 bg-grid-navy [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,black,transparent)]" />
      <header className="relative flex items-center justify-between px-5 sm:px-10 py-6">
        <div className="flex items-center gap-3">
          <NovaMark size={34} />
          <span className="text-sm font-extrabold tracking-tight text-nova-navy">NOVA OPSIS</span>
        </div>
        <div className="flex items-center gap-2" data-testid="onboarding-progress">
          {steps.map((s, i) => (
            <span
              key={s.title}
              className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
                i <= step ? "w-8 bg-nova-blue" : "w-4 bg-slate-200"
              }`}
            />
          ))}
        </div>
      </header>

      <main className="relative flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.45, ease: EASE }}
            >
              <p className="text-[11px] font-bold tracking-[0.28em] uppercase text-nova-blue">
                Step {step + 1} of {steps.length}
              </p>
              <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter text-nova-navy">
                {steps[step].title}
              </h1>
              <p className="mt-3 text-sm sm:text-base text-nova-slate">{steps[step].sub}</p>

              <div className="mt-9">{steps[step].content}</div>

              {error && (
                <p data-testid="onboarding-error" className="mt-5 text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                  {error}
                </p>
              )}

              <div className="mt-10 flex items-center justify-between">
                <button
                  type="button"
                  data-testid="onboarding-back-btn"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 text-sm font-medium text-nova-muted hover:text-nova-navy transition-colors disabled:opacity-0"
                >
                  <ArrowLeft size={15} />
                  Back
                </button>

                {step < steps.length - 1 ? (
                  <motion.button
                    type="button"
                    data-testid="onboarding-continue-btn"
                    onClick={() => canContinue && setStep((s) => s + 1)}
                    disabled={!canContinue}
                    whileHover={{ scale: canContinue ? 1.03 : 1 }}
                    whileTap={{ scale: canContinue ? 0.97 : 1 }}
                    className="inline-flex items-center gap-2 rounded-full bg-nova-blue px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgb(37,99,235,0.35)] hover:bg-nova-bluedark transition-colors duration-300 disabled:opacity-40 disabled:shadow-none"
                  >
                    Continue
                    <ArrowRight size={15} />
                  </motion.button>
                ) : (
                  <motion.button
                    type="button"
                    data-testid="onboarding-finish-btn"
                    onClick={finish}
                    disabled={loading}
                    whileHover={{ scale: loading ? 1 : 1.03 }}
                    whileTap={{ scale: loading ? 1 : 0.97 }}
                    className="inline-flex items-center gap-2 rounded-full bg-nova-blue px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgb(37,99,235,0.35)] hover:bg-nova-bluedark transition-colors duration-300 disabled:opacity-60"
                  >
                    {loading && <Loader2 size={16} className="animate-spin" />}
                    Set Up My Workspace
                    {!loading && <ArrowRight size={15} />}
                  </motion.button>
                )}
              </div>

              {step === 2 && tools.length === 0 && (
                <p className="mt-4 text-xs text-nova-muted text-right">
                  You can skip this — Nova's built-in tools cover you.
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
